import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMe, logout as logoutRequest } from "../../features/auth/api/auth";
import { authQueryKeys } from "../../features/auth/api/query-keys";
import {
  canAccessPermission,
  resolveTargetWindow,
} from "../../shared/permissions";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: authQueryKeys.me(),
    queryFn: getMe,
    retry: false,
  });

  const profile = data?.profile || null;
  const error =
    queryError?.response?.status === 401
      ? "unauthenticated"
      : queryError?.message || null;

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      queryClient.setQueryData(authQueryKeys.me(), null);
    }
  }, [queryClient]);

  const isAuthenticated = !!profile;

  const can = useCallback(
    (permission) => canAccessPermission(profile?.job_details, permission),
    [profile?.job_details],
  );

  const targetWindow = useMemo(
    () => resolveTargetWindow(profile?.job_details),
    [profile?.job_details],
  );

  const value = {
    profile,
    loading,
    error,
    isAuthenticated,
    can,
    targetWindow,
    logout,
    refetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
